import type {
  IInternalFormSchema,
  IInternalSchemaField
} from '../../api/internal-field-contracts';
import { internalFieldsApi } from '../../api/internal-fields-api';
import { InternalFieldDisplay } from '../../components/field-controls';
import { flatInternalPayload } from '../../internal-field-values';
import { refusalMessage } from '../../refusal';
import { headlineSuffixOf } from '../person-hub/research-headline';

/**
 * The staff fields a work item is read with (SR-222, F9's Task 8; CMP-09): a survey queue's
 * column picker offers its survey's staff form, and an item's pane says the participant's
 * Eligibility — the Quality rating and the Travel that Remove writes.
 *
 * **What a queue can show is the survey's schema** (`internalFieldsApi.schema`, F2's one read per
 * survey per page load), less what a row cannot carry: a multi-select, which survey-api leaves out
 * of `internal` (it has no flat reading), and a managed field, whose value the hub derives in
 * memory while the list reads only stored values — a column of it would say "—" for a value the
 * hub shows. An admin-only field is absent from a staff caller's schema already.
 *
 * **Every offered field is asked for**, shown or not (`fields=`, one code each). Rejected: asking
 * only the shown ones, which would put the picker's state into the query and read the page again
 * on every box ticked.
 *
 * **Quality rating and Travel are shown by default**, found by survey-api's suffix rule
 * (`headlineSuffixOf`, F2's mirror): they are the two fields a Remove writes, so they are what
 * the queue is worked by. Approval status, the third headline, is the hub's and a box away.
 */

/** Why All and My tasks offer no staff field: a field is a survey's, and their rows are several
 *  surveys'. */
export const STAFF_FIELDS_NOTE = 'Choose a queue to add its staff fields.';

/** The fields a queue's rows can carry, in the form's order. */
export function staffColumnFields(schema: IInternalFormSchema): IInternalSchemaField[] {
  return schema.groups
    .flatMap((group) => group.fields)
    .filter((field) => field.type !== 'multi_select' && field.managedBy === null);
}

/** A queue's staff fields, or what stopped them being read — the queue still draws without them. */
export interface IStaffColumns {
  fields: IInternalSchemaField[];
  error: Nullable<string>;
}

export async function readStaffColumns(surveyCode: string): Promise<IStaffColumns> {
  try {
    return { fields: staffColumnFields(await internalFieldsApi.schema(surveyCode)), error: null };
  } catch (failure) {
    return { fields: [], error: refusalMessage(failure, 'Could not read the staff fields') };
  }
}

/** The two fields a Remove writes — a queue's default staff columns, and the pane's
 *  Eligibility, Quality rating first. */
export function removeWrites(fields: readonly IInternalSchemaField[]): IInternalSchemaField[] {
  return (['quality_rating', 'travel'] as const).flatMap((suffix) =>
    fields.filter((field) => headlineSuffixOf(field.code) === suffix)
  );
}

/** One staff value as a row carries it — `text` absent where the participant has none. */
export function StaffValue({
  text,
  field
}: {
  text: string | undefined;
  field: IInternalSchemaField;
}) {
  if (text === undefined) return <span className="text-muted-foreground">—</span>;
  const payload = flatInternalPayload(text, field.type);
  return payload ? (
    <InternalFieldDisplay payload={payload} options={field.options} />
  ) : (
    <span className="wrap-anywhere">{text}</span>
  );
}
